/**
 * useDocumentTitle — sets the browser tab title dynamically.
 *
 * Format: "(N) Title — ToToDo" when count > 0
 *         "Title — ToToDo" otherwise
 *
 * Features:
 * - Title updates reactively when count changes
 * - Visibility-change blinking for inbox/invites (optional)
 * - Cleans up on unmount (restores default)
 *
 * @see §3 in OBSERVABILITY_ANALYTICS_REMINDERS_PLAN.md
 */

import { useEffect, useRef } from "react"

const APP_NAME = "ToToDo"
const DEFAULT_TITLE = APP_NAME

interface UseDocumentTitleOptions {
  /** View/page title (e.g. "Today", "Inbox", project name) */
  title: string
  /** Optional item count to show as prefix */
  count?: number
  /** If true, blinks the tab title when hidden (for inbox notifications etc.) */
  blinkOnHidden?: boolean
  /** Custom blink message (default: "📬 New items!") */
  blinkMessage?: string
}

export function useDocumentTitle({
  title,
  count,
  blinkOnHidden = false,
  blinkMessage = "📬 New items!",
}: UseDocumentTitleOptions): void {
  const blinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const normalTitleRef = useRef("")

  useEffect(() => {
    // Format: "(N) Title — ToToDo" or "Title — ToToDo"
    const prefix = count && count > 0 ? `(${count}) ` : ""
    const fullTitle = `${prefix}${title} — ${APP_NAME}`
    normalTitleRef.current = fullTitle
    document.title = fullTitle

    return () => {
      // Restore default on unmount
      document.title = DEFAULT_TITLE
    }
  }, [title, count])

  // Blink effect when tab is hidden
  useEffect(() => {
    if (!blinkOnHidden || !count || count <= 0) return

    const handleVisibility = () => {
      if (document.hidden) {
        // Start blinking
        let showBlink = false
        blinkIntervalRef.current = setInterval(() => {
          showBlink = !showBlink
          document.title = showBlink
            ? blinkMessage
            : normalTitleRef.current
        }, 1500)
      } else {
        // Stop blinking, restore normal title
        if (blinkIntervalRef.current) {
          clearInterval(blinkIntervalRef.current)
          blinkIntervalRef.current = null
        }
        document.title = normalTitleRef.current
      }
    }

    document.addEventListener("visibilitychange", handleVisibility)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility)
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current)
        blinkIntervalRef.current = null
      }
    }
  }, [blinkOnHidden, blinkMessage, count])
}
